import React, { useEffect, useRef, useState } from "react";
import {
  PLANS,
  PLAN_PRICES,
  type BillingCycle,
  type PlanCurrency,
  type PlanId,
} from "./landingContent";
import { trackLanding } from "./landingAnalytics";
import { useLandingCopy } from "./landingCopy";
import { Emphasis, SectionShell } from "./LandingSections";

const CURRENCIES: PlanCurrency[] = ["USD", "THB"];
const MONTHS_PER_YEAR = 12;

const CURRENCY_SYMBOL: Record<PlanCurrency, string> = { USD: "$", THB: "฿" };

/** "$14.99" → "$0", "149.000₫" → "0₫" — the free plan's price in the region's own format. */
export const formatFreePrice = (price: { monthly: string }): string =>
  price.monthly.replace(/[\d.,]+/, "0");

/** "$7.99", "$69", "฿1,990": symbols never localize, so prices read the same everywhere. */
export const formatPlanPrice = (amount: number, currency: PlanCurrency): string =>
  CURRENCY_SYMBOL[currency] +
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);

/** Whole-number percentage saved by paying yearly instead of twelve months. */
export const yearlySavingPercent = (monthly: number, yearly: number): number =>
  Math.round((1 - yearly / (monthly * MONTHS_PER_YEAR)) * 100);

const paidPrices = (plan: PlanId, currency: PlanCurrency) =>
  plan === "pro" ? PLAN_PRICES[currency].pro : PLAN_PRICES[currency].global;

interface PlansSectionProps {
  /** Language of the page; Thai visitors start on baht. */
  initialCurrency?: PlanCurrency;
  regionId?: string;
  onRegionChange?: (id: string) => void;
  onAction: (action: string) => void;
}

const PlansSection: React.FC<PlansSectionProps> = ({
  initialCurrency,
  regionId,
  onAction,
}) => {
  const { tr } = useLandingCopy();
  const [currency, setCurrency] = useState<PlanCurrency>(
    initialCurrency || (regionId === "TH" ? "THB" : "USD"),
  );
  const [cycle, setCycle] = useState<BillingCycle>("yearly");
  const gridRef = useRef<HTMLDivElement>(null);

  // One "pricing viewed" per visit, the first time the plans scroll into view.
  useEffect(() => {
    const node = gridRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        trackLanding("pricing_viewed");
        observer.disconnect();
      },
      { threshold: 0.3 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const bestSaving = Math.max(
    ...(["pro", "global"] as const).map((plan) => {
      const { monthly, yearly } = paidPrices(plan, currency);
      return yearlySavingPercent(monthly, yearly);
    }),
  );
  const matchPrice = formatPlanPrice(PLAN_PRICES[currency].energyMatch, currency);
  const matchProPrice = formatPlanPrice(PLAN_PRICES[currency].energyMatchPro, currency);

  return (
    <SectionShell id="plans">
      <div className="al-head" data-reveal>
        <div className="al-eyebrow al-eyebrow--center">{tr(PLANS.eyebrow)}</div>
        <h2 className="al-h2">
          <Emphasis markup={tr(PLANS.headline)} />
        </h2>
        <p className="al-lead">{tr(PLANS.body)}</p>
      </div>

      <div className="al-plan-controls" data-reveal>
        <div className="al-switch" role="group" aria-label={tr("Monthly")}>
          {(["monthly", "yearly"] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={cycle === value}
              className={cycle === value ? "is-on" : ""}
              onClick={() => setCycle(value)}
            >
              {tr(value === "monthly" ? "Monthly" : "Yearly")}
              {value === "yearly" && (
                <em className="al-switch__save">
                  {tr("Save")} {bestSaving}%
                </em>
              )}
            </button>
          ))}
        </div>
        <div className="al-switch al-switch--small" role="group" aria-label="Currency">
          {CURRENCIES.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={currency === value}
              className={currency === value ? "is-on" : ""}
              onClick={() => setCurrency(value)}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      <div className="al-plans" ref={gridRef}>
        {PLANS.tiers.map((tier) => {
          const isPaid = tier.id !== "free";
          const prices = isPaid ? paidPrices(tier.id, currency) : null;
          const amount = prices ? prices[cycle] : 0;
          const saving = prices ? yearlySavingPercent(prices.monthly, prices.yearly) : 0;
          return (
            <div
              key={tier.id}
              className={`al-plan al-plan--${tier.id}`}
              data-reveal
            >
              {tier.badge && <span className="al-plan__kicker">{tr(tier.badge)}</span>}
              <h3 className="al-plan__name">{tr(tier.name)}</h3>
              <p className="al-plan__headline">{tr(tier.headline)}</p>

              <div className="al-price">
                <b className="al-price__amount">{formatPlanPrice(amount, currency)}</b>
                <span className="al-price__unit">
                  {tr(cycle === "yearly" && isPaid ? "per year" : "per month")}
                </span>
                {prices && cycle === "yearly" && (
                  <small className="al-price__note">
                    <span dir="ltr">
                      ≈ {formatPlanPrice(prices.yearly / MONTHS_PER_YEAR, currency)}
                    </span>{" "}
                    {tr("per month")}
                    {saving > 0 && (
                      <em className="al-ribbon">
                        {tr("Save")} {saving}%
                      </em>
                    )}
                  </small>
                )}
              </div>

              {tier.id === "pro" && <p className="al-plan__trial">{tr(PLANS.trialNote)}</p>}
              <ul className="al-plan__features">
                {tier.features.map((feature) => (
                  <li key={feature}>{tr(feature)}</li>
                ))}
              </ul>
              <button
                type="button"
                className={`al-btn ${tier.id === "pro" ? "al-btn--primary" : "al-btn--ghost"} al-plan__cta`}
                onClick={() => {
                  trackLanding("plan_selected", { plan: tier.id });
                  onAction(tier.cta.action);
                }}
              >
                {tr(tier.cta.label)}
                {tier.id === "pro" ? " ✦" : ""}
              </button>
            </div>
          );
        })}
      </div>

      <p className="al-note al-note--pass" data-reveal>
        {tr(PLANS.globalPass)}
      </p>

      <div className="al-match" data-reveal>
        <div className="al-match__copy">
          <b>{tr("Energy Match™")}</b>
          <h3>{tr(PLANS.energyMatch.headline)}</h3>
          <p>
            {tr(PLANS.energyMatch.text)} · {tr(PLANS.energyMatch.included)}
          </p>
        </div>
        <div className="al-match__buy">
          <span className="al-match__price">
            <b dir="ltr">{matchPrice}</b> {tr(PLANS.energyMatch.perMatch)} ·{" "}
            <span dir="ltr">
              {tr("Pro")} {matchProPrice}
            </span>
          </span>
          <button
            type="button"
            className="al-btn al-btn--ghost"
            onClick={() => {
              trackLanding("plan_selected", { plan: "energy_match" });
              onAction(PLANS.energyMatch.cta.action);
            }}
          >
            {tr(PLANS.energyMatch.cta.label)}
          </button>
        </div>
      </div>

      <p className="al-fineprint" data-reveal>
        {tr(PLANS.fineprint)}
      </p>
    </SectionShell>
  );
};

export default PlansSection;
